import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { toast } from 'sonner';

// ============================================================================
// Types
// ============================================================================

export type DeliverableStatus =
  | 'pending'
  | 'in_progress'
  | 'in_review'
  | 'revision'
  | 'approved'
  | 'delivered';

export interface Deliverable {
  id: string;
  tenant_id: string;
  project_id: string;
  title: string;
  description: string | null;
  status: DeliverableStatus;
  assigned_to: string | null;
  status_changed_at: string;
  reviewer: string | null;
  scope_id: string | null;
  scope_name: string | null;
  billable_item_id: string | null;
  submission_id: string | null;
  submission_name: string | null;
  start_date: string | null;
  due_date: string | null;
  sort_order: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // Phase 10e (V118): Acceptance tracking — undefined means pre-migration data
  accepted_at?: string | null;
  accepted_by_email?: string | null;
  // R4: Portal visibility — opt-in flag, false by default
  portal_visible?: boolean;
  // Phase 10f (V123): Linked site visits — only present on single-deliverable fetch
  linked_site_visits?: Array<{
    id: string;
    title: string;
    visit_date: string;
    visit_type: string;
    status: string;
    visited_by: string | null;
  }>;
}

export interface DeliverableCreate {
  title: string;
  description?: string;
  status?: DeliverableStatus;
  assigned_to?: string;
  reviewer?: string;
  scope_id?: string;
  billable_item_id?: string;
  submission_id?: string;
  start_date?: string;
  due_date?: string;
  sort_order?: number;
}

export interface DeliverableUpdate {
  title?: string;
  description?: string | null;
  status?: DeliverableStatus;
  assigned_to?: string | null;  // null = clear the field
  reviewer?: string | null;     // null = clear the field
  scope_id?: string | null;     // null = clear the field
  billable_item_id?: string;
  submission_id?: string;
  start_date?: string | null;
  due_date?: string | null;
  sort_order?: number;
  // R4: portal visibility — opt-in per deliverable
  portal_visible?: boolean;
}

export interface DeliverableSummary {
  pending: number;
  in_progress: number;
  in_review: number;
  revision: number;
  approved: number;
  delivered: number;
  total: number;
}

// ============================================================================
// Query Hooks
// ============================================================================

export function useDeliverables(projectId: string | undefined) {
  return useQuery({
    queryKey: ['deliverables', projectId],
    queryFn: async () => {
      const response = await api.deliverables.list(projectId!);
      return response.deliverables as Deliverable[];
    },
    enabled: !!projectId,
  });
}

export function useDeliverable(deliverableId: string | undefined) {
  return useQuery({
    queryKey: ['deliverable', deliverableId],
    queryFn: async () => {
      const response = await api.deliverables.get(deliverableId!);
      return response.deliverable as Deliverable;
    },
    enabled: !!deliverableId,
  });
}

export function useDeliverableSummary(projectId: string | undefined) {
  return useQuery({
    queryKey: ['deliverables', 'summary', projectId],
    queryFn: async () => {
      const response = await api.deliverables.summary(projectId!);
      return response.summary as DeliverableSummary;
    },
    enabled: !!projectId,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

export function useCreateDeliverable() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { projectId: string; deliverable: DeliverableCreate }) =>
      api.deliverables.create(data.projectId, data.deliverable),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliverables'] });
      toast.success('Deliverable created');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create deliverable');
    },
  });
}

export function useUpdateDeliverable() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: DeliverableUpdate }) =>
      api.deliverables.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliverables'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update deliverable');
    },
  });
}

export function useStaleDeliverables(days: number = 3) {
  return useQuery({
    queryKey: ['deliverables', 'stale', days],
    queryFn: async () => {
      const response = await api.deliverables.stale(days);
      return response.deliverables as (Deliverable & { project_name: string })[];
    },
  });
}

export interface DeliverableDashboardFilters {
  assigned_to?: string;
  reviewer?: string;
  search?: string;
}

export function useDeliverablesDashboard(filters?: DeliverableDashboardFilters) {
  return useQuery({
    queryKey: ['deliverables', 'dashboard', filters?.assigned_to, filters?.reviewer, filters?.search],
    queryFn: async () => {
      const response = await api.deliverables.dashboard(filters);
      return {
        deliverables: response.deliverables as (Deliverable & { project_name: string })[],
        total: response.total as number,
        stale_count: response.stale_count as number,
        stale_deliverables: response.stale_deliverables as (Deliverable & { project_name: string })[],
      };
    },
  });
}

export function useDeleteDeliverable() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (deliverableId: string) =>
      api.deliverables.delete(deliverableId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliverables'] });
      toast.success('Deliverable deleted');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete deliverable');
    },
  });
}
